import { CheckCircle } from "lucide-react";

import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { HubLinkListCopy } from "@components/bluetooth-landing/HubLinkList";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";
import {
  contactFormPl,
  mvpClusterLinks,
  mvpKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/mvp-landing/shared";

export const title =
  "Budowa MVP dla startupu - aplikacje, urządzenia IoT i produkty software";

export const description =
  "Budowa MVP dla startupu: aplikacja mobilna, webowa, SaaS lub urządzenie IoT. Polski software house — warsztaty produktowe, wycena i szybka walidacja pomysłu.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Budowa MVP dla startupu",
    description,
    "MVP product development",
  ),
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Usługi MVP i budowa produktu od zera",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "MVP aplikacji lub urządzenia",
        url: "https://bluesbrackets.com/mvp-aplikacji-lub-urzadzenia",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Warsztaty produktowe",
        url: "https://bluesbrackets.com/warsztaty-produktowe",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Cennik MVP",
        url: "https://bluesbrackets.com/cennik-mvp",
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Artykuły o MVP i wycenie produktu",
    itemListElement: mvpKnowledgeBaseLinks.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.text,
      url: `https://bluesbrackets.com${item.href}`,
    })),
  },
];

export const navbarCopy: NavbarCopy = {
  links: {
    targetAudience: "Dla kogo",
    values: "Baza wiedzy",
    services: "Usługi",
    process: "Proces",
    about: "O nas",
    faq: "FAQ",
  },
  cta: "Wyceń MVP",
};

export const heroCopy: HeroCopy = {
  header: "Budowa MVP",
  headerTail: "dla startupu",
  description:
    "Pomagamy przejść od pomysłu do działającego produktu: aplikacja mobilna, webowa, SaaS albo urządzenie IoT. Warsztaty, wycena i MVP pod walidację z prawdziwymi użytkownikami.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Zobacz usługi MVP",
    href: "#uslugi",
  },
  benefits: [
    { text: "App, SaaS albo urządzenie", Icon: CheckCircle },
    { text: "Warsztaty przed wyceną", Icon: CheckCircle },
    { text: "Wąski zakres, szybki feedback", Icon: CheckCircle },
  ],
};

export const introCopy = {
  id: "dla-kogo",
  eyebrow: "Dla kogo",
  heading: "Gdy masz pomysł, ale jeszcze nie wiesz dokładnie, od czego zacząć",
  paragraphs: [
    "Pierwszy kontakt wygląda zwykle podobnie: chcesz proste MVP do testu z użytkownikami, orientacyjną wycenę i timeline, czasem NDA przed briefem albo warsztaty, żeby uporządkować zakres. Nie szukasz jednej technologii — szukasz partnera, który pomoże zwalidować pomysł bez przepalania budżetu.",
    "Tu znajdziesz budowę MVP aplikacji i urządzeń, warsztaty produktowe oraz orientacyjny cennik. Proces jest ten sam: najpierw discovery i wąski zakres, potem działający produkt i feedback z rynku — niezależnie od tego, czy startujesz z PWA, Flutterem czy prototypem hardware.",
  ],
};

export const servicesListCopy: HubLinkListCopy = {
  id: "uslugi",
  eyebrow: "Usługi MVP",
  heading: "Od czego chcesz zacząć?",
  intro:
    "Wybierz stronę najbliższą Twojemu etapowi — budowa MVP, warsztaty przed wyceną albo orientacyjne widełki kosztów.",
  items: [
    {
      href: "/mvp-aplikacji-lub-urzadzenia",
      title: "MVP aplikacji lub urządzenia - szybka walidacja pomysłu",
      description:
        "MVP aplikacji mobilnej, webowej, SaaS albo urządzenia IoT — jeden proces walidacji niezależnie od formy produktu.",
    },
    {
      href: "/warsztaty-produktowe",
      title:
        "Warsztaty produktowe - zakres, ryzyka i wycena przed budową MVP",
      description:
        "Discovery, user stories i makiety low-fi przed wyceną — gdy brief jest niepełny albo budżet wymaga twardego zakresu.",
    },
    {
      href: "/cennik-mvp",
      title:
        "Ile kosztuje MVP? Wycena aplikacji, urządzenia IoT lub platformy",
      description:
        "Orientacyjne widełki kosztów MVP: web, mobile, SaaS z AI i produkt connected — plus co najbardziej winduje budżet.",
    },
  ],
};

export const blogListCopy: HubLinkListCopy = {
  id: "wartosci",
  eyebrow: "Baza wiedzy",
  heading: "Przeczytaj przed pierwszym spotkaniem",
  intro:
    "Krótkie przewodniki: czym jest MVP, ile może kosztować i jak przygotować brief, żeby wycena miała sens.",
  items: [
    {
      href: "/pl/blogs/co-to-jest-mvp",
      title: "Co to jest MVP i czym różni się od prototypu?",
      description:
        "MVP vs prototyp vs PoC vs produkt produkcyjny — kiedy którego potrzebujesz i czego unikać przy pierwszej wersji.",
    },
    {
      href: "/pl/blogs/ile-kosztuje-mvp-aplikacji",
      title: "Ile kosztuje MVP aplikacji? Przykłady zakresów i budżetów",
      description:
        "Orientacyjne widełki kosztów MVP web, mobile i SaaS — oraz przykłady zakresów, od których warto zacząć.",
    },
    {
      href: "/pl/blogs/jak-przygotowac-sie-do-wyceny-aplikacji",
      title: "Jak przygotować się do wyceny aplikacji lub produktu IoT?",
      description:
        "Brief, warsztaty produktowe i materiały, dzięki którym oferty da się uczciwie porównać.",
    },
  ],
};

export const processCopy = {
  id: "proces",
  eyebrow: "Proces",
  heading: "Od pomysłu do feedbacku z rynku",
  steps: [
    {
      title: "Konsultacja i discovery",
      description:
        "30 minut rozmowy: pomysł, użytkownicy, ograniczenia budżetowe i timeline. Decydujemy, czy wystarczy wycena, czy najpierw warsztaty.",
    },
    {
      title: "Zakres MVP i wycena",
      description:
        "Wąski, mierzalny zakres — kryteria sukcesu, ryzyka, stack i widełki budżetowe. Fixed price albo Time & Material, zależnie od pewności wymagań.",
    },
    {
      title: "Budowa, launch i iteracja",
      description:
        "Działający produkt do testów z użytkownikami, a potem kolejne iteracje na podstawie feedbacku — nie „wersja 1.0 na wszystko”.",
    },
  ],
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Masz pomysł, ale nie wiesz jeszcze, od czego zacząć?",
    p: "Umów się na darmową konsultację — opowiedz o produkcie, a zaproponujemy warsztaty, wycenę albo zakres pierwszego MVP.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Budowa MVP: aplikacje, SaaS i urządzenia IoT. Polski software house — od warsztatów po walidację z użytkownikami.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Usługi MVP" },
      { href: "#wartosci", text: "Baza wiedzy" },
      { href: "#proces", text: "Proces współpracy" },
      ...mvpClusterLinks.filter(l => l.href !== "/mvp"),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy MVP",
    links: [...mvpKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
